import React, { useState, useEffect } from 'react';
import { branding } from '../../constants/branding';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { store } from '../../store';

interface StrategyTemplate {
  name: string;
  display_name: string;
  description: string;
  category: string;
  parameters_schema: any;
  default_parameters: any;
}

interface StrategyConfig {
  bot_id: number;
  strategy_name: string;
  parameters: any;
  is_active: boolean;
}

interface StrategyPreviewProps {
  botId: number;
  botName: string;
  symbol: string;
  /** Strategy code from retail bot list (e.g. MA_CROSS) — used if preview API is unavailable */
  strategyHint?: string;
}

function buildRetailStrategyFallback(
  botId: number,
  strategyHint: string
): { config: StrategyConfig; template: StrategyTemplate } | null {
  const raw = (strategyHint || '').trim();
  if (!raw) return null;
  const name = raw.toUpperCase().replace(/\s+/g, '_');

  type Preset = { category: string; displayName: string; description: string; parameters: Record<string, number> };
  const presets: Record<string, Preset> = {
    MA_CROSS: {
      category: 'trend_following',
      displayName: 'Moving Average Cross',
      description:
        'Typical defaults from your bot listing. Exact parameters appear once the strategy preview API is deployed on your server.',
      parameters: {
        short_window: 10,
        long_window: 30,
        position_size_pct: 10,
        stop_loss_pct: 2,
        take_profit_pct: 5,
      },
    },
    RSI: {
      category: 'mean_reversion',
      displayName: 'RSI',
      description:
        'Typical defaults from your bot listing. Exact parameters appear once the strategy preview API is deployed on your server.',
      parameters: {
        rsi_period: 14,
        oversold: 30,
        overbought: 70,
        position_size_pct: 10,
        stop_loss_pct: 2,
        take_profit_pct: 5,
      },
    },
    BOLLINGER: {
      category: 'mean_reversion',
      displayName: 'Bollinger Bands',
      description:
        'Typical defaults from your bot listing. Exact parameters appear once the strategy preview API is deployed on your server.',
      parameters: {
        period: 20,
        std_dev: 2,
        position_size_pct: 10,
        stop_loss_pct: 2,
        take_profit_pct: 5,
      },
    },
  };

  const preset =
    presets[name] || {
      category: 'custom',
      displayName: raw.replace(/_/g, ' '),
      description:
        'Summary from your bot listing. Exact parameters appear once the strategy preview API is deployed on your server.',
      parameters: { position_size_pct: 10, stop_loss_pct: 2, take_profit_pct: 5 },
    };

  const config: StrategyConfig = {
    bot_id: botId,
    strategy_name: name,
    parameters: preset.parameters,
    is_active: true,
  };
  const template: StrategyTemplate = {
    name,
    display_name: preset.displayName,
    description: preset.description,
    category: preset.category,
    parameters_schema: {},
    default_parameters: {},
  };
  return { config, template };
}

const StrategyPreview: React.FC<StrategyPreviewProps> = ({ botId, botName, symbol, strategyHint }) => {
  const [strategyConfig, setStrategyConfig] = useState<StrategyConfig | null>(null);
  const [strategyTemplate, setStrategyTemplate] = useState<StrategyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      setLoading(true);
      try {
        const apiBaseUrl = getApiBaseUrl();
        const state = store.getState();
        const token = state.auth.token;
        const authType = state.auth.type;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(
          `${apiBaseUrl}/api/retail/trading/bots/${botId}/strategy/preview`,
          { credentials: 'include', headers, signal: ac.signal }
        );

        if (response.ok) {
          const body = await response.json();
          const payload = body.data;
          const cfg = payload?.config;
          const tpl = payload?.template;

          if (cfg && typeof cfg === 'object' && cfg.strategy_name) {
            setStrategyConfig(cfg as StrategyConfig);
            if (tpl && typeof tpl === 'object') {
              setStrategyTemplate(tpl as StrategyTemplate);
            } else {
              const sn = String(cfg.strategy_name);
              setStrategyTemplate({
                name: sn,
                display_name: sn.replace(/_/g, ' '),
                description: '',
                category: 'custom',
                parameters_schema: {},
                default_parameters: {},
              });
            }
            return;
          }
        }

        const fallback = strategyHint ? buildRetailStrategyFallback(botId, strategyHint) : null;
        if (fallback) {
          setStrategyConfig(fallback.config);
          setStrategyTemplate(fallback.template);
        } else {
          setStrategyConfig(null);
          setStrategyTemplate(null);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('Failed to fetch strategy config:', err);
        const fallback = strategyHint ? buildRetailStrategyFallback(botId, strategyHint) : null;
        if (fallback) {
          setStrategyConfig(fallback.config);
          setStrategyTemplate(fallback.template);
        } else {
          setStrategyConfig(null);
          setStrategyTemplate(null);
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [botId, strategyHint]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'trend_following': return '#2196f3';
      case 'mean_reversion': return '#ff9800';
      case 'momentum': return '#4caf50';
      case 'custom': return '#9c27b0';
      default: return '#666';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'trend_following': return '📈';
      case 'mean_reversion': return '🔄';
      case 'momentum': return '⚡';
      case 'custom': return '⚙️';
      default: return '🤖';
    }
  };

  const formatParameterValue = (paramName: string, value: any) => {
    if (typeof value === 'number') {
      if (paramName.includes('pct') || paramName.includes('percent')) {
        return `${value}%`;
      }
      return value.toString();
    }
    return value;
  };

  if (loading) {
    return (
      <div style={{ 
        padding: 12, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 4, 
        textAlign: 'center' 
      }}>
        Loading strategy information...
      </div>
    );
  }

  if (!strategyConfig || !strategyTemplate) {
    return (
      <div style={{ 
        padding: 12, 
        backgroundColor: '#fff3cd', 
        borderRadius: 4, 
        border: '1px solid #ffeaa7' 
      }}>
        <strong>⚠️ No Strategy Configuration</strong>
        <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#856404' }}>
          This bot doesn't have a strategy configured yet.
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      border: `1px solid ${branding.borderColor}`, 
      borderRadius: 8, 
      overflow: 'hidden' 
    }}>
      {/* Strategy Header */}
      <div style={{ 
        padding: 12, 
        backgroundColor: '#f8f9fa', 
        borderBottom: `1px solid ${branding.borderColor}`,
        cursor: 'pointer'
      }} onClick={() => setShowDetails(!showDetails)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>{getCategoryIcon(strategyTemplate.category)}</span>
              <strong>{strategyTemplate.display_name}</strong>
              <span style={{ 
                fontSize: 12, 
                padding: '2px 8px', 
                backgroundColor: getCategoryColor(strategyTemplate.category),
                color: 'white',
                borderRadius: 12
              }}>
                {strategyTemplate.category.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#666' }}>
              {strategyTemplate.description}
            </p>
          </div>
          <span style={{ fontSize: 18, color: '#666' }}>
            {showDetails ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {/* Strategy Details */}
      {showDetails && (
        <div style={{ padding: 16 }}>
          {/* Trading Rules */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 16 }}>Trading Rules</h4>
            <div style={{ 
              padding: 12, 
              backgroundColor: '#e3f2fd', 
              borderRadius: 4,
              fontSize: 14
            }}>
              {strategyConfig.strategy_name === 'MA_CROSS' && (
                <div>
                  <p><strong>🟢 BUY Signal:</strong> When {strategyConfig.parameters.short_window}-period MA crosses above {strategyConfig.parameters.long_window}-period MA</p>
                  <p><strong>🔴 SELL Signal:</strong> When {strategyConfig.parameters.short_window}-period MA crosses below {strategyConfig.parameters.long_window}-period MA</p>
                </div>
              )}
              {strategyConfig.strategy_name === 'RSI' && (
                <div>
                  <p><strong>🟢 BUY Signal:</strong> When RSI ({strategyConfig.parameters.rsi_period} periods) recovers from oversold level ({strategyConfig.parameters.oversold})</p>
                  <p><strong>🔴 SELL Signal:</strong> When RSI ({strategyConfig.parameters.rsi_period} periods) declines from overbought level ({strategyConfig.parameters.overbought})</p>
                </div>
              )}
              {strategyConfig.strategy_name === 'BOLLINGER' && (
                <div>
                  <p><strong>🟢 BUY Signal:</strong> When price touches lower Bollinger Band ({strategyConfig.parameters.period} periods, {strategyConfig.parameters.std_dev}σ)</p>
                  <p><strong>🔴 SELL Signal:</strong> When price touches upper Bollinger Band ({strategyConfig.parameters.period} periods, {strategyConfig.parameters.std_dev}σ)</p>
                </div>
              )}
            </div>
          </div>

          {/* Strategy Parameters */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 16 }}>Configuration</h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 12 
            }}>
              {Object.entries(strategyConfig.parameters).map(([paramName, value]) => (
                <div key={paramName} style={{ 
                  padding: 8, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 4,
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase' }}>
                    {paramName.replace(/_/g, ' ')}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>
                    {formatParameterValue(paramName, value)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Information */}
          <div style={{ 
            padding: 12, 
            backgroundColor: '#fff3cd', 
            borderRadius: 4,
            border: '1px solid #ffeaa7'
          }}>
            <h5 style={{ margin: '0 0 8px 0', color: '#856404' }}>⚠️ Risk Information</h5>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: '#856404' }}>
              <li>Position size: {strategyConfig.parameters.position_size_pct}% of available balance per trade</li>
              <li>Strategy category: {strategyTemplate.category.replace('_', ' ')}</li>
              <li>This is an automated trading strategy - past performance doesn't guarantee future results</li>
              <li>Monitor your allocations regularly and adjust based on market conditions</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyPreview;
